import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import type { Submission, Client, Form } from '../types.js';
import { listSubmissions, getClient, deleteSubmission, exportCsvUrl, formsApi } from '../api.js';

export default function SubmissionsList() {
  const { clientId } = useParams<{ clientId: string }>();
  const [client, setClient] = useState<Client | null>(null);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [forms, setForms] = useState<Form[]>([]);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [formId, setFormId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  async function load() {
    if (!clientId) return;
    setLoading(true);
    setError('');
    try {
      const [subRes, clientRes, formsRes] = await Promise.all([
        listSubmissions(clientId, { page, status: status || undefined, form_id: formId || undefined }),
        client ? Promise.resolve({ client }) : getClient(clientId),
        formsApi.list(clientId),
      ]);
      setSubmissions(subRes.submissions);
      if (!client) setClient(clientRes.client);
      setForms(formsRes.forms);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, [clientId, page, status, formId]);

  async function handleDelete(id: string) {
    if (!confirm('Delete this submission? It will be permanently removed after 7 days.')) return;
    try {
      await deleteSubmission(id);
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  function emailBadge(s: Submission['email_status']) {
    if (s === 'sent')     return <span className="badge badge-green">Sent</span>;
    if (s === 'failed')   return <span className="badge badge-red">Failed</span>;
    if (s === 'pending')  return <span className="badge badge-yellow">Pending</span>;
    return <span className="badge badge-grey">—</span>;
  }

  function webhookBadge(s: Submission['webhook_status']) {
    if (s === 'sent')     return <span className="badge badge-green">Sent</span>;
    if (s === 'failed')   return <span className="badge badge-red">Failed</span>;
    if (s === 'pending')  return <span className="badge badge-yellow">Pending</span>;
    return <span className="badge badge-grey">—</span>;
  }

  const fields = client?.form_schema.fields.slice(0, 3) ?? [];

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">
          {client ? `Submissions — ${client.name}` : 'Submissions'}
        </h1>
        <div style={{ display: 'flex', gap: '.5rem' }}>
          {clientId && (
            <a href={exportCsvUrl(clientId)} className="btn btn-secondary">Export CSV</a>
          )}
          {client && (
            <Link to={`/clients/${client.id}`} className="btn btn-secondary">← Client</Link>
          )}
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: '.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} style={{ width: 'auto' }}>
            <option value="">All statuses</option>
            <option value="email_failed">Email failed</option>
            <option value="webhook_failed">Webhook failed</option>
            <option value="pending">Pending</option>
          </select>
          <select value={formId} onChange={(e) => { setFormId(e.target.value); setPage(1); }} style={{ width: 'auto' }}>
            <option value="">All forms</option>
            {forms.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name} {f.is_primary ? '(primary)' : ''}
              </option>
            ))}
          </select>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {loading ? (
          <p style={{ color: '#94a3b8' }}>Loading…</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Submitted</th>
                {fields.map((f) => <th key={f.name}>{f.label}</th>)}
                <th>Email</th>
                <th>Webhook</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {submissions.length === 0 && (
                <tr><td colSpan={fields.length + 4} style={{ color: '#94a3b8', padding: '1rem 0' }}>No submissions found.</td></tr>
              )}
              {submissions.map((s) => (
                <tr key={s.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {s.is_test && <span className="badge badge-yellow" style={{ marginRight: '.4rem' }}>🧪 Test</span>}
                    {new Date(s.created_at * 1000).toLocaleString('en-AU')}
                  </td>
                  {fields.map((f) => (
                    <td key={f.name} style={{ maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {String(s.form_data[f.name] ?? '')}
                    </td>
                  ))}
                  <td>{emailBadge(s.email_status)}</td>
                  <td>{webhookBadge(s.webhook_status)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <Link to={`/submissions/${s.id}`} className="btn btn-secondary btn-sm" style={{ marginRight: '.4rem' }}>View</Link>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(s.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="pagination">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>← Prev</button>
          <span>Page {page}</span>
          <button onClick={() => setPage((p) => p + 1)} disabled={submissions.length < 25}>Next →</button>
        </div>
      </div>
    </>
  );
}
